import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'umi';
import { Layout } from 'tea-component';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import SortBySelect from '@op/components/SelectComponents/SortBySelect';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import useIntls from '@opm/hooks/useIntls';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { UgcCreativeTrendsPoint } from '@opm/operationLog/pages';
import { goToDetailsPage } from '@opm/utils/history';
import { DBO_TM, DBO_U } from '@op/utils/format';
import { useTopicTypeColorMap } from '@opm/pages/CreativeTrends/hooks';
import StatusTabs from '@opm/pages/CreativeTrends/components/StatusTabs';
import ViewSwitcher from '@opm/pages/CreativeTrends/components/ViewSwitcher';
import CardView from '@opm/pages/CreativeTrends/CardView';
import ListView from '@opm/pages/CreativeTrends/ListView';
import TrendDrawer from '@opm/pages/CreativeTrends/TrendDrawer';
import {
  useUgcTrendDetail,
  useUgcTrendsData,
  useUgcTrendsStats,
} from './hooks';
import { ITrendItemBase } from '@opm/pages/CreativeTrends/types';
import { ITrendItem, trendKey } from './types';
import {
  LIST_FILTER_KEYS,
  SORT_OPTIONS,
  STATUS_TAB_OPTIONS,
  UGC_KPI_CONFIGS,
  UGC_LIST_METRIC_COLUMNS,
} from './constants';
import UgcTrendVideoThumb from './components/UgcTrendVideoThumb';
import '@opm/pages/CreativeTrends/index.scss';
import '@opm/pages/CreativeTrends/CardView/index.scss';
import '@opm/pages/CreativeTrends/CardView/VideoCover/index.scss';
import '@opm/pages/CreativeTrends/ListView/index.scss';
import '@opm/pages/CreativeTrends/components/PreviewThumbnails.scss';
import '@opm/pages/CreativeTrends/TrendDrawer/index.scss';
import './index.scss';
import { TranslateConfigProvider } from '@opm/hooks/useTranslateConfig';
import { ugcCreativeTrendsProcessor } from './processors';
import { useUGCGameMap } from '@opm/utils/gameMap';

const { Content } = Layout;

const userDefaultValue = {
  ...DBO_TM.filterRangeDay(13, 0),
  date_type: 'daily',
};

export default function UgcCreativeTrends() {
  DBOM_POINT.useInitPage({ buriedPoint: UgcCreativeTrendsPoint });
  const { intl } = useIntls();

  const {
    filter,
    setFilter,
    loading: filterLoading,
    ...filterProps
  } = useFilterCommon({
    keys: [...LIST_FILTER_KEYS],
  });
  const { gameMap } = useUGCGameMap();
  // Topic Type chip 配色映射（UGC 用 ugc_trends_config_topic_types，与 CT 走相同的调色板）
  const { topicTypeColorMap } = useTopicTypeColorMap(
    'ugc_trends_config_topic_types',
  );

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ITrendItem | null>(null);
  const dispatch = useDispatch();

  const drawerFilter = useMemo(() => {
    if (!drawerVisible || !selectedRecord?.topic) return undefined;
    return {
      ...filter,
      game_id: selectedRecord.game_id,
      topic: selectedRecord.topic,
      source_type: selectedRecord.source_type || 'ugc',
    };
  }, [drawerVisible, selectedRecord, filter]);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    dispatch({
      type: 'opinionMarketingMenu/toggleMenuExpand',
      payload: { menuExpand: true },
    });
  }, []);

  const {
    cardRecords,
    tableRecords,
    total,
    listMax,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading: dataLoading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    statusTab,
    setStatusTab,
    sortBy,
    setSortBy,
    keyword,
    setKeyword,
    viewMode,
    setViewMode,
  } = useUgcTrendsData({
    filter,
    defaultViewMode: 'card',
    closeDrawer: handleDrawerClose,
  });

  const { counts } = useUgcTrendsStats({ filter, searchText: keyword });
  const { data: detailData } = useUgcTrendDetail({
    filter: drawerFilter,
  });

  const handleCardClick = useCallback(
    (record: ITrendItemBase) => {
      setSelectedRecord(record as ITrendItem);
      setDrawerVisible(true);
      DBOM_POINT.btn('card_click', {
        game_id: record.game_id,
        topic: record.topic,
      });
      dispatch({
        type: 'opinionMarketingMenu/toggleMenuExpand',
        payload: { menuExpand: false },
      });
    },
    [dispatch],
  );

  const handleRowClick = useCallback((record: ITrendItemBase) => {
    setSelectedRecord(record as ITrendItem);
    setDrawerVisible(true);
    DBOM_POINT.btn('row_click', {
      game_id: record.game_id,
      topic: record.topic,
    });
    dispatch({
      type: 'opinionMarketingMenu/toggleMenuExpand',
      payload: { menuExpand: false },
    });
  }, [dispatch]);

  const handleGoToDetail = useCallback(
    (record: ITrendItemBase, type = 'list') => {
      setDrawerVisible(false);
      DBOM_POINT.btn(
        type === 'drawer' ? 'drawer_view_all' : 'drawer_title_click',
        { game_id: record.game_id, topic: record.topic },
      );
      goToDetailsPage({
        filter: {
          ...filter,
          game_id: record.game_id,
          topic: record.topic,
          topic_en: record.topic_en,
          source_type: record.source_type || 'ugc',
        },
        navigateUrl: '/marketing/UgcCreativeTrends/Details',
      });
    },
    [filter],
  );

  const handleSortChange = useCallback(
    (value: string) => {
      const option = SORT_OPTIONS.find((o) => o.value === value);
      const order = (option?.order || 'desc') as 'asc' | 'desc';
      setSortBy({ by: value, order });
      DBOM_POINT.btn('sort_change', { sort_item: value, sort_model: order });
    },
    [setSortBy],
  );

  const handleStatusTabChange = useCallback(
    (tab: any) => {
      setStatusTab(tab);
      DBOM_POINT.btn('status_tab_change', { tab });
    },
    [setStatusTab],
  );

  const handleViewModeChange = useCallback(
    (mode: 'card' | 'table') => {
      setViewMode(mode);
      DBOM_POINT.btn('view_switch', { mode });
    },
    [setViewMode],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setKeyword(value);
      if (value) {
        DBOM_POINT.btn('search', { keyword: value });
      }
    },
    [setKeyword],
  );

  return (
    <TranslateConfigProvider processor={ugcCreativeTrendsProcessor}>
      <ScrollBottomList>
        <Content className="dbom-ugc-trends-page">
          <Content.Header title={intl('UGC Trends')} />
          <MarketContentBody
            loading={filterLoading}
            filterSlot={
              <FilterCommon
                {...filterProps}
                userDefaultValue={userDefaultValue}
                loadingHeight={150}
                useOriDefaultValue
                realTimeMode={true}
                confirm={(value: any) => {
                  setFilter(value);
                }}
              />
            }
          >
            <div className="dbom-ct-row">
              <div className="dbom-ct-row-left">
                <FilterBoxCommon
                  left={[
                    {
                      key: 'segment',
                      defaultValue: statusTab || 'hot',
                      element: (
                        <StatusTabs
                          value={statusTab}
                          onChange={handleStatusTabChange}
                          counts={counts}
                          tabOptions={STATUS_TAB_OPTIONS}
                        />
                      ),
                    },
                    {
                      key: 'sortByElement',
                      defaultValue: sortBy?.by || 'tweets_view',
                      element: (
                        <SortBySelect
                          options={SORT_OPTIONS}
                          defaultSortBy={sortBy}
                          changeSortBy={(newSortBy: any) =>
                            handleSortChange(newSortBy?.by)
                          }
                          className="dbom-ct-sort-select"
                        />
                      ),
                    },
                  ]}
                  right={[
                    {
                      key: 'search',
                      defaultValue: keyword,
                      defaultParams: {
                        placeholder: intl('Search Trends'),
                        style: { width: 240 },
                        onSearch: (value: string) => handleSearch(value),
                        onClear: () => handleSearch(''),
                      },
                    },
                    {
                      key: 'viewSwitcher',
                      defaultValue: viewMode || 'card',
                      element: (
                        <ViewSwitcher
                          value={viewMode}
                          onChange={handleViewModeChange}
                        />
                      ),
                    },
                  ]}
                  onChange={() => { }}
                />
                <div className={`dbom-ct-content ${drawerVisible ? 'dbom-ct-content--drawer-open' : ''}`} >
                  <div className="dbom-ct-content__main">
                    {viewMode === 'card' ? (
                      <CardView
                        data={cardRecords}
                        listMax={listMax}
                        gameMap={gameMap}
                        topicTypeColorMap={topicTypeColorMap}
                        isFirstLoading={isFirstLoading}
                        isEmpty={isEmpty}
                        isPaginationLoading={isPaginationLoading}
                        nextPage={loadNextPage}
                        onCardClick={handleCardClick}
                        onTitleClick={(record) =>
                          handleGoToDetail(record, 'list')
                        }
                        selectedTrendKey={
                          drawerVisible ? trendKey(selectedRecord) : undefined
                        }
                        filter={filter}
                        kpiConfigs={UGC_KPI_CONFIGS}
                        thumbnailsAccessor={(r: any) => r?.top_related_videos}
                        renderThumbnail={({ item }) => (
                          <UgcTrendVideoThumb item={item} />
                        )}
                      />
                    ) : (
                      <ListView
                        data={tableRecords}
                        total={total}
                        loading={!!dataLoading}
                        listMax={listMax}
                        gameMap={gameMap}
                        topicTypeColorMap={topicTypeColorMap}
                        pageInfo={pageInfo}
                        changePageInfo={changeTablePageInfo}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        filter={filter}
                        onRowClick={handleRowClick}
                        onTitleClick={(record) =>
                          handleGoToDetail(record, 'list')
                        }
                        selectedTrendKey={
                          drawerVisible ? trendKey(selectedRecord) : undefined
                        }
                        metricColumns={UGC_LIST_METRIC_COLUMNS}
                        thumbnailsAccessor={(r: any) => r?.top_related_videos}
                        onThumbnailClick={(item, record) => {
                          DBO_U.open(item.video_url);
                        }}
                        shareIcon
                      />
                    )}
                  </div>
                  {drawerVisible && (
                    <TrendDrawer
                      record={selectedRecord}
                      detailData={detailData}
                      filter={drawerFilter}
                      gameMap={gameMap}
                      topicTypeColorMap={topicTypeColorMap}
                      onClose={handleDrawerClose}
                      onViewAll={(record) => handleGoToDetail(record, 'drawer')}
                      onTitleClick={(record) =>
                        handleGoToDetail(record, 'drawer')
                      }
                      kpiConfigs={UGC_KPI_CONFIGS}
                    />
                  )}
                </div>
              </div>
            </div>
          </MarketContentBody>
        </Content>
      </ScrollBottomList>
    </TranslateConfigProvider>
  );
}
